import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { aeoGeo } from "@/lib/seo/schema";

export const Route = createFileRoute("/en")({
  head: () => ({
    meta: [
      { title: "Fenny — Premium video on subscription, at market pace" },
      {
        name: "description",
        content:
          "Premium video on subscription. Strategy, production and delivery in one rhythm — fixed monthly price, first cut within 14 days.",
      },
      { property: "og:title", content: "Fenny — Premium video on subscription" },
      {
        property: "og:description",
        content:
          "Strategy, production and delivery in the same rhythm. Fixed monthly price. First cut within 14 days.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/en" },
      { property: "og:locale", content: "en_GB" },
      { name: "language", content: "en" },
    ],
    links: [
      { rel: "canonical", href: "https://fenny.se/en" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en", name: "Fenny — Premium video on subscription, at market pace", description: "Premium video on subscription. Strategy, production and delivery in one rhythm — fixed monthly price, first cut within 14 days."})) },
    ],
  }),
  component: EnHome,
});

function EnHome() {
  return (
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-mint/10 via-transparent to-transparent" />
        <div className="container-wide relative pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-mint">Premium video on subscription</div>
            <h1 className="mt-5 text-balance font-serif text-[44px] italic leading-[1.05] tracking-[-0.02em] md:text-[72px]">
              Video that drives the business, in the rhythm of the market.
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Strategy, production and delivery in the same rhythm — fixed monthly price,
              no quotes, first cut within 14 days. The same English-speaking team behind
              films for Garageportexperten, Bolt, Huawei, Renova and Wimeck.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/en/contact"
                className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-mint-foreground transition-transform hover:-translate-y-[1px]"
              >
                Book a strategy call <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/en/work"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-foreground/90 hover:bg-white/5"
              >
                See our work
              </Link>
            </div>
            <p className="mt-5 text-xs text-muted-foreground">
              Full English-speaking crew · EUR or SEK billing · Reply within 24h during office hours (weekdays 08:30–17:00 CET)
            </p>
          </div>
        </div>
      </section>

      {/* Service triad */}
      <section className="border-t border-white/10">
        <div className="container-wide grid gap-6 py-20 md:grid-cols-3">
          <ServiceCard
            tone="mint"
            eyebrow="Subscription"
            title="Pulse"
            body="Premium video flow on a fixed monthly fee. 4–10 finished films per month, versioned for paid social, web and OOH. From SEK 14,995/month."
            cta="Read more about Pulse"
            to="/en/pulse"
          />
          <ServiceCard
            tone="peach"
            eyebrow="Project"
            title="Prime"
            body="Custom productions when you need a bigger swing — brand films, commercials, documentary, recruitment. Fixed price, 3–8 weeks delivery."
            cta="Read more about Prime"
            to="/en/prime"
          />
          <ServiceCard
            tone="butter"
            eyebrow="Pricing"
            title="Transparent packages"
            body="No hidden fees, no agency markups. See exactly what you get on every tier and run the numbers in our calculator."
            cta="See pricing"
            to="/en/pricing"
          />
        </div>
      </section>

      {/* Trust band */}
      <section className="border-t border-white/10 bg-surface/40">
        <div className="container-wide py-16">
          <div className="text-xs uppercase tracking-[0.22em] text-mint">Production partner to</div>
          <h2 className="mt-3 max-w-3xl text-balance text-2xl font-semibold leading-tight md:text-4xl">
            Garageportexperten, Bolt, Huawei, Renova, Wimeck and more.
          </h2>
          <p className="mt-4 max-w-2xl text-sm text-muted-foreground md:text-base">
            We make video for brands that can't afford to look mediocre — and don't have
            weeks to wait for the first cut.
          </p>
        </div>
      </section>

      {/* Two delivery models */}
      <section className="border-t border-white/10">
        <div className="container-wide grid gap-10 py-20 md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-mint">How delivery works</div>
            <h2 className="mt-3 font-serif text-3xl italic leading-tight md:text-5xl">
              Two models, one team.
            </h2>
            <p className="mt-5 text-muted-foreground">
              <strong className="text-foreground">Pulse</strong> — strategy session,
              first cut in 14 days, then 4–10 films per month at a fixed monthly fee.
              Versioning for paid social, OOH and broadcast included.
            </p>
            <p className="mt-4 text-muted-foreground">
              <strong className="text-foreground">Prime</strong> — brief → treatment →
              fixed quote → production → post → delivery in 3–8 weeks. One-off campaigns,
              brand films, hero spots.
            </p>
          </div>
          <ul className="grid gap-3 self-center">
            {[
              ["Local Stockholm crew", "Producers, directors and DPs based in Stockholm. No travel fees within Sweden."],
              ["Bilingual delivery", "Swedish and English by default. Additional languages on request."],
              ["English-speaking team", "Briefs, treatments, reviews and contracts in English. EUR or SEK billing."],
              ["Reply within 24h", "Real humans, not chatbots. We reply during office hours, weekdays 08:30–17:00 CET."],
            ].map(([title, body]) => (
              <li key={title} className="rounded-2xl border border-white/10 bg-surface/60 p-5">
                <div className="font-semibold">{title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-white/10 bg-gradient-to-b from-surface/40 to-background">
        <div className="container-wide py-20 text-center">
          <h2 className="font-serif text-3xl italic leading-tight md:text-5xl">
            Need premium video, in English?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            30 minutes to map scope, timeline and budget. We respond in English within 24 hours, during office hours (weekdays 08:30–17:00 CET).
          </p>
          <div className="mt-7">
            <Link
              to="/en/contact"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-7 py-3.5 text-sm font-semibold text-mint-foreground"
            >
              Book a strategy call <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
            <Link to="/en/video-agency-stockholm" className="hover:text-mint">Video agency Stockholm</Link>
            <span>·</span>
            <Link to="/en/film-production-sweden" className="hover:text-mint">Film production Sweden</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({
  eyebrow,
  title,
  body,
  cta,
  to,
  tone,
}: {
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
  to: string;
  tone: "mint" | "peach" | "butter";
}) {
  const toneClass =
    tone === "mint"
      ? "from-mint/20 to-transparent"
      : tone === "peach"
        ? "from-peach/20 to-transparent"
        : "from-butter/20 to-transparent";
  return (
    <div className={`relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br ${toneClass} p-7`}>
      <div className="text-xs uppercase tracking-[0.22em] text-mint">{eyebrow}</div>
      <h3 className="mt-3 font-serif text-3xl italic">{title}</h3>
      <p className="mt-3 text-sm text-muted-foreground">{body}</p>
      <Link to={to} className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-mint">
        {cta} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
